import {
  getMobileAppDeploymentPlatformLabel,
  mobileAppDeploymentAppOptions,
  mobileAppDeploymentPlatformOptions,
  mobileAppDeploymentTypeFilterOptions,
  type MobileAppDeploymentApp,
  type MobileAppDeploymentListItem,
  type MobileAppDeploymentPlatform,
  type MobileAppDeploymentTypeFilter,
} from '@dev-console/types';
import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState } from 'react';

import { getMobileAppDeployments } from '@/config/api-helpers';

export const meta = { label: 'Forced Upgrade', order: 6 };

type UseTarget = MobileAppDeploymentPlatform;

const STATSIG_CONFIG_URL_BY_APP: Record<MobileAppDeploymentApp, string> = {
  patient: 'https://console.statsig.com/6RFekk7eSu2Gu59L5p04xx/dynamic_configs/mobile_app_minimum_supported_deployment',
  provider:
    'https://console.statsig.com/6RFekk7eSu2Gu59L5p04xx/dynamic_configs/provider_app_minimum_supported_deployment',
};

function formatDate(value: string) {
  return new Intl.DateTimeFormat('en-US', {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(new Date(value));
}

function platformLabel(platform: MobileAppDeploymentPlatform) {
  return getMobileAppDeploymentPlatformLabel(platform);
}

function isApplicableToPlatform(deployment: MobileAppDeploymentListItem, platform: Exclude<UseTarget, 'all'>) {
  return deployment.platform === 'all' || deployment.platform === platform;
}

function getEasIdSummary(deployment: MobileAppDeploymentListItem) {
  const ids = [
    deployment.groupUpdateId ? `Group ${deployment.groupUpdateId}` : null,
    deployment.iosUpdateId ? `iOS update ${deployment.iosUpdateId}` : null,
    deployment.androidUpdateId ? `Android update ${deployment.androidUpdateId}` : null,
    deployment.iosEasBuildId ? `iOS build ${deployment.iosEasBuildId}` : null,
    deployment.androidEasBuildId ? `Android build ${deployment.androidEasBuildId}` : null,
  ].filter(Boolean);

  return ids.length > 0 ? ids.join(' / ') : 'None';
}

function SegmentButton<T extends string>({
  active,
  label,
  onClick,
  value,
}: {
  active: boolean;
  label: string;
  onClick: (value: T) => void;
  value: T;
}) {
  return (
    <button
      type='button'
      onClick={() => onClick(value)}
      className={`h-9 rounded px-3 text-sm transition-colors ${
        active
          ? 'bg-neutral-900 text-white'
          : 'border border-neutral-200 bg-white text-neutral-700 hover:border-neutral-400'
      }`}
    >
      {label}
    </button>
  );
}

function UseDeploymentButton({
  deployment,
  isSelected,
  onSelect,
  target,
}: {
  deployment: MobileAppDeploymentListItem;
  isSelected: boolean;
  onSelect: (deployment: MobileAppDeploymentListItem, target: UseTarget) => void;
  target: UseTarget;
}) {
  const isApplicable =
    target === 'all'
      ? deployment.platform === 'all'
      : isApplicableToPlatform(deployment, target === 'ios' ? 'ios' : 'android');
  const label = getMobileAppDeploymentPlatformLabel(target);

  return (
    <button
      type='button'
      aria-pressed={isSelected}
      disabled={!isApplicable}
      onClick={() => onSelect(deployment, target)}
      className={`h-7 min-w-16 rounded-md border px-2 text-xs transition-colors ${
        isSelected
          ? 'border-neutral-900 bg-neutral-900 text-white'
          : isApplicable
          ? 'border-neutral-200 bg-white text-neutral-700 hover:border-neutral-400'
          : 'cursor-not-allowed border-neutral-100 bg-neutral-50 text-neutral-300'
      }`}
      aria-label={`Use ${deployment.deploymentRunKey} for ${label}`}
    >
      {label}
    </button>
  );
}

export function MobileAppUpgradePage() {
  const [app, setApp] = useState<MobileAppDeploymentApp>('patient');
  const [platform, setPlatform] = useState<MobileAppDeploymentPlatform>('all');
  const [deploymentType, setDeploymentType] = useState<MobileAppDeploymentTypeFilter>('all');
  const [page, setPage] = useState(1);
  const [iosDeploymentRunKey, setIosDeploymentRunKey] = useState('');
  const [androidDeploymentRunKey, setAndroidDeploymentRunKey] = useState('');
  const [copied, setCopied] = useState(false);
  const textAreaRef = useRef<HTMLTextAreaElement>(null);

  const { data, error, isLoading, refetch } = useQuery({
    queryKey: ['mobile-app-deployments', app, platform, deploymentType, page],
    queryFn: () => getMobileAppDeployments({ app, platform, deploymentType, page }),
  });

  const deployments = useMemo(() => data?.deployments ?? [], [data?.deployments]);

  useEffect(() => {
    setPage(1);
  }, [app, deploymentType, platform]);

  // Run keys belong to one app, so a selection made for the other app must not reach its Statsig config.
  useEffect(() => {
    setIosDeploymentRunKey('');
    setAndroidDeploymentRunKey('');
    setCopied(false);
  }, [app]);

  const statsigJson = useMemo(
    () =>
      JSON.stringify(
        {
          iosMinimumSupportedDeploymentRunKey: iosDeploymentRunKey,
          androidMinimumSupportedDeploymentRunKey: androidDeploymentRunKey,
        },
        null,
        2
      ),
    [androidDeploymentRunKey, iosDeploymentRunKey]
  );

  function selectDeployment(deployment: MobileAppDeploymentListItem, target: UseTarget) {
    if (target === 'all') {
      if (deployment.platform !== 'all') {
        return;
      }

      setIosDeploymentRunKey(deployment.deploymentRunKey);
      setAndroidDeploymentRunKey(deployment.deploymentRunKey);
      return;
    }

    if (!isApplicableToPlatform(deployment, target)) {
      return;
    }

    if (target === 'ios') {
      setIosDeploymentRunKey(deployment.deploymentRunKey);
      return;
    }

    setAndroidDeploymentRunKey(deployment.deploymentRunKey);
  }

  async function copyStatsigJson() {
    try {
      await navigator.clipboard.writeText(statsigJson);
      setCopied(true);
    } catch {
      textAreaRef.current?.focus();
      textAreaRef.current?.select();
      setCopied(false);
    }
  }

  return (
    <div className='space-y-5'>
      <div className='flex flex-wrap items-center justify-between gap-3'>
        <div>
          <h2 className='font-mediumbold text-xl text-neutral-900'>Mobile App Upgrade</h2>
          <div className='mt-1 text-sm text-neutral-500'>
            {data?.sourceEnvironment === 'production' ? 'Production deployment rows' : 'Local/configured database'}
          </div>
        </div>
        <button
          type='button'
          onClick={() => refetch()}
          className='h-9 rounded-md border border-neutral-200 bg-white px-3 text-sm text-neutral-700 shadow-sm hover:border-neutral-400'
        >
          Refresh
        </button>
      </div>

      <div className='flex flex-wrap items-center gap-3'>
        <div className='flex flex-wrap items-center gap-2'>
          <span className='text-sm text-neutral-500'>App</span>
          {mobileAppDeploymentAppOptions.map((filter) => (
            <SegmentButton
              key={filter.value}
              active={app === filter.value}
              label={filter.label}
              value={filter.value}
              onClick={setApp}
            />
          ))}
        </div>
        <div className='flex flex-wrap items-center gap-2'>
          <span className='text-sm text-neutral-500'>Platform</span>
          {mobileAppDeploymentPlatformOptions.map((filter) => (
            <SegmentButton
              key={filter.value}
              active={platform === filter.value}
              label={filter.label}
              value={filter.value}
              onClick={setPlatform}
            />
          ))}
        </div>
        <div className='flex flex-wrap items-center gap-2'>
          <span className='text-sm text-neutral-500'>Type</span>
          {mobileAppDeploymentTypeFilterOptions.map((filter) => (
            <SegmentButton
              key={filter.value}
              active={deploymentType === filter.value}
              label={filter.label}
              value={filter.value}
              onClick={setDeploymentType}
            />
          ))}
        </div>
      </div>

      <div className='overflow-hidden rounded-lg border border-neutral-200 bg-white shadow-sm'>
        <div className='flex flex-wrap items-center justify-between gap-2 border-b border-neutral-100 px-4 py-3'>
          <div className='text-sm text-neutral-600'>Page {data?.pagination.page ?? page}</div>
          <div className='flex gap-2'>
            <button
              type='button'
              disabled={page <= 1}
              onClick={() => setPage((currentPage) => Math.max(currentPage - 1, 1))}
              className='h-8 rounded-md border border-neutral-200 bg-white px-3 text-sm text-neutral-700 hover:border-neutral-400 disabled:cursor-not-allowed disabled:border-neutral-100 disabled:text-neutral-300'
            >
              Previous
            </button>
            <button
              type='button'
              disabled={!data?.pagination.hasNextPage}
              onClick={() => setPage((currentPage) => currentPage + 1)}
              className='h-8 rounded-md border border-neutral-200 bg-white px-3 text-sm text-neutral-700 hover:border-neutral-400 disabled:cursor-not-allowed disabled:border-neutral-100 disabled:text-neutral-300'
            >
              Next
            </button>
          </div>
        </div>
        {isLoading ? (
          <div className='px-4 py-6 text-sm text-neutral-600'>Loading mobile app deployments...</div>
        ) : error ? (
          <div className='px-4 py-6 text-sm text-red-600'>
            {error instanceof Error ? error.message : 'Failed to load mobile app deployments'}
          </div>
        ) : deployments.length === 0 ? (
          <div className='px-4 py-6 text-sm text-neutral-600'>No mobile app deployments found.</div>
        ) : (
          <div className='overflow-x-auto'>
            <table className='min-w-full divide-y divide-neutral-100 text-left text-sm'>
              <thead className='bg-neutral-25 text-neutral-600'>
                <tr>
                  <th className='px-4 py-3 font-medium'>Created</th>
                  <th className='px-4 py-3 font-medium'>Platform</th>
                  <th className='px-4 py-3 font-medium'>Type</th>
                  <th className='px-4 py-3 font-medium'>App / Runtime</th>
                  <th className='px-4 py-3 font-medium'>Run key</th>
                  <th className='px-4 py-3 font-medium'>GitHub Actions</th>
                  <th className='px-4 py-3 font-medium'>EAS IDs</th>
                  <th className='px-4 py-3 font-medium'>Use</th>
                </tr>
              </thead>
              <tbody className='divide-y divide-neutral-100'>
                {deployments.map((deployment) => (
                  <tr key={deployment.id} className='hover:bg-neutral-25 align-top'>
                    <td className='whitespace-nowrap px-4 py-3 text-neutral-700'>{formatDate(deployment.createdAt)}</td>
                    <td className='px-4 py-3 text-neutral-700'>{platformLabel(deployment.platform)}</td>
                    <td className='px-4 py-3 text-neutral-700'>{deployment.deploymentType}</td>
                    <td className='px-4 py-3 text-neutral-700'>
                      <div>{deployment.appVersion}</div>
                      <div className='text-neutral-500'>{deployment.runtimeVersion}</div>
                    </td>
                    <td className='max-w-[260px] px-4 py-3 font-mono text-xs text-neutral-700'>
                      {deployment.deploymentRunKey}
                    </td>
                    <td className='px-4 py-3'>
                      <a
                        className='text-blue-700 hover:underline'
                        href={deployment.deploymentRunUrl}
                        rel='noreferrer'
                        target='_blank'
                      >
                        Open
                      </a>
                    </td>
                    <td className='max-w-[320px] px-4 py-3 text-xs text-neutral-600'>{getEasIdSummary(deployment)}</td>
                    <td className='px-4 py-3'>
                      <div
                        className='flex flex-wrap gap-1'
                        role='group'
                        aria-label={`Use ${deployment.deploymentRunKey}`}
                      >
                        <UseDeploymentButton
                          deployment={deployment}
                          isSelected={
                            iosDeploymentRunKey === deployment.deploymentRunKey &&
                            androidDeploymentRunKey === deployment.deploymentRunKey
                          }
                          target='all'
                          onSelect={selectDeployment}
                        />
                        <UseDeploymentButton
                          deployment={deployment}
                          isSelected={
                            iosDeploymentRunKey === deployment.deploymentRunKey &&
                            androidDeploymentRunKey !== deployment.deploymentRunKey
                          }
                          target='ios'
                          onSelect={selectDeployment}
                        />
                        <UseDeploymentButton
                          deployment={deployment}
                          isSelected={
                            androidDeploymentRunKey === deployment.deploymentRunKey &&
                            iosDeploymentRunKey !== deployment.deploymentRunKey
                          }
                          target='android'
                          onSelect={selectDeployment}
                        />
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <div className='rounded-lg border border-neutral-200 bg-white p-4 shadow-sm'>
        <div className='mb-2 flex flex-wrap items-center justify-between gap-2'>
          <h3 className='font-mediumbold text-base text-neutral-900'>Statsig JSON</h3>
          <div className='flex items-center gap-3'>
            {copied ? <span className='text-sm text-green-700'>Copied</span> : null}
            <button
              type='button'
              onClick={() => void copyStatsigJson()}
              className='h-9 rounded-md bg-neutral-900 px-3 text-sm text-white hover:bg-neutral-700'
            >
              Copy JSON
            </button>
          </div>
        </div>
        <textarea
          ref={textAreaRef}
          readOnly
          value={statsigJson}
          className='h-32 w-full resize-none rounded-md border border-neutral-200 bg-neutral-50 p-3 font-mono text-sm text-neutral-900 focus:outline-none'
        />
        <a
          className='mt-3 inline-block text-sm text-blue-700 hover:underline'
          href={STATSIG_CONFIG_URL_BY_APP[app]}
          target='_blank'
        >
          Open Statsig config
        </a>
      </div>
    </div>
  );
}
